/**
 * services/BetaApplicationService.ts
 * Beta application business logic and workflow orchestration
 *
 * Architecture: WU1.5 Task 3 - Pure business logic using repository pattern
 *
 * Handles all beta application business logic including:
 * - Application submission workflow
 * - Admin review and decision workflows
 * - Status change tracking
 * - Email notification queueing
 *
 * Pattern: Services orchestrate, repositories access data
 */

import { betaApplicationsRepository, ConflictError } from '@/repositories/betaApplicationsRepository';
import { betaApplicationStatusHistoryService } from './BetaApplicationStatusHistoryService';
import type {
  BetaApplication,
  BetaApplicationInsert,
  BetaApplicationUpdate,
  BetaApplicationStatus
} from '@/types/supabase/BetaApplication';

/**
 * Not found error for better error handling
 */
class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Beta Application Service
 *
 * Pure business logic service that orchestrates complex workflows.
 * All data access delegated to betaApplicationsRepository.
 *
 * Enhanced patterns:
 * - Optimistic concurrency handling with friendly error messages
 * - Orchestrated admin workflows (status + history + email)
 * - Email normalization handled by repository
 */
class BetaApplicationService {
  // ===== BASIC CRUD WRAPPER METHODS =====

  /**
   * Get application by ID
   *
   * @param id - Application ID
   * @returns Promise<BetaApplication | null>
   */
  async get(id: string): Promise<BetaApplication | null> {
    return betaApplicationsRepository.getById(id);
  }

  /**
   * Get application by email
   *
   * Email is normalized (trim + lowercase) by repository.
   *
   * @param email - Applicant's email address
   * @returns Promise<BetaApplication | null>
   */
  async getByEmail(email: string): Promise<BetaApplication | null> {
    return betaApplicationsRepository.getByEmail(email);
  }

  /**
   * Check if email has already applied
   *
   * Email is normalized (trim + lowercase) by repository.
   *
   * @param email - Email address to check
   * @returns Promise<boolean>
   */
  async hasApplied(email: string): Promise<boolean> {
    return betaApplicationsRepository.existsByEmail(email);
  }

  /**
   * List applications with pagination and filtering
   *
   * @param opts - List options (status, limit, offset, withTotal)
   * @returns Promise<{ items: BetaApplication[], total: number }>
   */
  async list(opts: {
    status?: BetaApplicationStatus;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  } = {}): Promise<{ items: BetaApplication[]; total: number }> {
    return betaApplicationsRepository.list(opts);
  }

  /**
   * Create new application
   *
   * Email is normalized (trim + lowercase) by repository.
   *
   * @param data - Application data
   * @returns Promise<BetaApplication>
   */
  async create(data: BetaApplicationInsert): Promise<BetaApplication> {
    return betaApplicationsRepository.create(data);
  }

  /**
   * Update application (basic update without workflow)
   *
   * @param id - Application ID
   * @param updates - Partial updates
   * @param prevUpdatedAt - Previous updatedAt for concurrency check
   * @returns Promise<BetaApplication>
   * @throws ConflictError if application was modified by another admin
   */
  async update(
    id: string,
    updates: BetaApplicationUpdate,
    prevUpdatedAt: string
  ): Promise<BetaApplication> {
    return betaApplicationsRepository.update(id, updates, prevUpdatedAt);
  }

  /**
   * Delete application
   *
   * @param id - Application ID
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    return betaApplicationsRepository.delete(id);
  }

  /**
   * Get all applications (backward compatibility)
   *
   * @returns Promise<BetaApplication[]>
   * @deprecated Use list() instead for pagination support
   */
  async getAll(): Promise<BetaApplication[]> {
    const { items } = await betaApplicationsRepository.list({ withTotal: false });
    return items;
  }

  /**
   * Add new application (for anonymous submissions)
   *
   * Uses SECURITY DEFINER function to allow anonymous users to submit
   * applications without direct table access. This is the method used
   * by the public beta application wizard.
   *
   * For authenticated/admin submissions, use create() instead.
   *
   * @param data - Application data
   * @returns Promise<BetaApplication>
   */
  async add(data: BetaApplicationInsert): Promise<BetaApplication> {
    return betaApplicationsRepository.submitApplication(data);
  }

  // ===== SPECIALIZED QUERY METHODS =====

  /**
   * Get applications by status
   *
   * @param status - Application status to filter by
   * @returns Promise<BetaApplication[]>
   * @deprecated Use list({ status }) instead for pagination support
   */
  async getByStatus(status: BetaApplicationStatus): Promise<BetaApplication[]> {
    return betaApplicationsRepository.getByStatus(status);
  }

  /**
   * Get application status counts
   *
   * @returns Promise<Record<BetaApplicationStatus, number>>
   */
  async getStatusCounts(): Promise<Record<BetaApplicationStatus, number>> {
    return betaApplicationsRepository.getStatusCounts();
  }

  /**
   * Get applications needing email notifications
   *
   * @returns Promise<BetaApplication[]>
   */
  async getApplicationsNeedingEmails(): Promise<BetaApplication[]> {
    return betaApplicationsRepository.getApplicationsNeedingEmails();
  }

  /**
   * Get email statistics
   *
   * Uses repository list method with filters.
   *
   * @returns Promise<email statistics>
   */
  async getEmailStats(): Promise<{
    totalDecisions: number;
    emailsSent: number;
    emailsPending: number;
  }> {
    // Get all applications with decisions
    const { items: decisionsWithPending } = await betaApplicationsRepository.list({
      withTotal: false
    });

    const decisions = decisionsWithPending.filter(app => app.status !== 'pending');
    const sent = decisions.filter(app => app.emailStatus === 'sent');

    return {
      totalDecisions: decisions.length,
      emailsSent: sent.length,
      emailsPending: decisions.length - sent.length
    };
  }

  // ===== ORCHESTRATED ADMIN WORKFLOW METHODS =====

  /**
   * Approve application (orchestrated workflow)
   *
   * Workflow:
   * 1. Get current application (for concurrency check)
   * 2. Update status via repository (with concurrency check)
   * 3. Record status history
   * 4. Queue email notification (TODO)
   *
   * @param id - Application ID
   * @param adminId - ID of admin performing the action
   * @param notes - Optional admin notes
   * @returns Promise<BetaApplication>
   * @throws NotFoundError if application not found
   * @throws ConflictError if application was modified by another admin (with reload data)
   */
  async approve(id: string, adminId: string, notes?: string): Promise<BetaApplication> {
    // 1. Get current application for optimistic concurrency
    const current = await betaApplicationsRepository.getById(id);
    if (!current) {
      throw new NotFoundError('Application not found');
    }

    try {
      // 2. Update status via repository (with concurrency check)
      const updated = await betaApplicationsRepository.update(
        id,
        {
          status: 'approved',
          reviewedBy: adminId,
          reviewedAt: new Date().toISOString(),
          adminNotes: notes ?? null
        },
        current.updatedAt // Optimistic concurrency
      );

      // 3. Record status history (only if status actually changed)
      if (current.status !== 'approved') {
        await betaApplicationStatusHistoryService.recordStatusChange(
          id,
          current.status,
          'approved',
          adminId,
          notes
        );
      }

      // 4. Queue email notification
      // TODO: [CONTRACTOR] Integrate with EmailService for automated email notifications

      // TODO: [CONTRACTOR] Implement admin action analytics tracking

      return updated;
    } catch (error) {
      // CONFLICT HANDLING PATTERN: Map repo errors to friendly messages
      if (error instanceof ConflictError) {
        // Reload current entity for UI refresh
        const reloaded = await betaApplicationsRepository.getById(id);
        throw new ConflictError(
          'This application was modified by another admin. The page will reload with current data.',
          reloaded ? { current: reloaded } : undefined
        );
      }
      throw error; // Re-throw other errors
    }
  }

  /**
   * Reject application (orchestrated workflow)
   *
   * Workflow:
   * 1. Get current application (for concurrency check)
   * 2. Update status via repository (with concurrency check)
   * 3. Record status history
   * 4. Queue email notification (TODO)
   *
   * @param id - Application ID
   * @param adminId - ID of admin performing the action
   * @param notes - Optional admin notes
   * @returns Promise<BetaApplication>
   * @throws NotFoundError if application not found
   * @throws ConflictError if application was modified by another admin (with reload data)
   */
  async reject(id: string, adminId: string, notes?: string): Promise<BetaApplication> {
    const current = await betaApplicationsRepository.getById(id);
    if (!current) {
      throw new NotFoundError('Application not found');
    }

    try {
      const updated = await betaApplicationsRepository.update(
        id,
        {
          status: 'rejected',
          reviewedBy: adminId,
          reviewedAt: new Date().toISOString(),
          adminNotes: notes ?? null
        },
        current.updatedAt
      );

      if (current.status !== 'rejected') {
        await betaApplicationStatusHistoryService.recordStatusChange(
          id,
          current.status,
          'rejected',
          adminId,
          notes
        );
      }

      // TODO: [CONTRACTOR] Queue rejection email

      return updated;
    } catch (error) {
      if (error instanceof ConflictError) {
        const reloaded = await betaApplicationsRepository.getById(id);
        throw new ConflictError(
          'This application was modified by another admin. The page will reload with current data.',
          reloaded ? { current: reloaded } : undefined
        );
      }
      throw error;
    }
  }

  /**
   * Waitlist application (orchestrated workflow)
   *
   * Workflow:
   * 1. Get current application (for concurrency check)
   * 2. Update status via repository (with concurrency check)
   * 3. Record status history
   * 4. Queue email notification (TODO)
   *
   * @param id - Application ID
   * @param adminId - ID of admin performing the action
   * @param notes - Optional admin notes
   * @returns Promise<BetaApplication>
   * @throws NotFoundError if application not found
   * @throws ConflictError if application was modified by another admin (with reload data)
   */
  async waitlist(id: string, adminId: string, notes?: string): Promise<BetaApplication> {
    const current = await betaApplicationsRepository.getById(id);
    if (!current) {
      throw new NotFoundError('Application not found');
    }

    try {
      const updated = await betaApplicationsRepository.update(
        id,
        {
          status: 'waitlisted',
          reviewedBy: adminId,
          reviewedAt: new Date().toISOString(),
          adminNotes: notes ?? null
        },
        current.updatedAt
      );

      if (current.status !== 'waitlisted') {
        await betaApplicationStatusHistoryService.recordStatusChange(
          id,
          current.status,
          'waitlisted',
          adminId,
          notes
        );
      }

      // TODO: [CONTRACTOR] Queue waitlist email

      return updated;
    } catch (error) {
      if (error instanceof ConflictError) {
        const reloaded = await betaApplicationsRepository.getById(id);
        throw new ConflictError(
          'This application was modified by another admin. The page will reload with current data.',
          reloaded ? { current: reloaded } : undefined
        );
      }
      throw error;
    }
  }

  /**
   * Update application status (generic workflow for backward compatibility)
   *
   * @param id - Application ID
   * @param status - New status
   * @param adminId - ID of admin performing the action
   * @param notes - Optional admin notes
   * @returns Promise<BetaApplication>
   * @deprecated Use approve(), reject(), or waitlist() instead for orchestrated workflows
   */
  async updateStatus(
    id: string,
    status: BetaApplicationStatus,
    adminId: string,
    notes?: string
  ): Promise<BetaApplication> {
    // Delegate to appropriate orchestrated method
    switch (status) {
      case 'approved':
        return this.approve(id, adminId, notes);
      case 'rejected':
        return this.reject(id, adminId, notes);
      case 'waitlisted':
        return this.waitlist(id, adminId, notes);
      default:
        throw new Error(`Unsupported status transition: ${status}`);
    }
  }

  /**
   * Update application status with email tracking (legacy method)
   *
   * @deprecated Use approve(), reject(), or waitlist() instead
   */
  async updateStatusWithEmail(
    id: string,
    status: BetaApplicationStatus,
    adminId: string,
    notes?: string
  ): Promise<BetaApplication> {
    return this.updateStatus(id, status, adminId, notes);
  }
}

// Export singleton instance
export const betaApplicationService = new BetaApplicationService();

// Export class for type imports
export { BetaApplicationService };

// Export error classes for component use
export { ConflictError, NotFoundError };
